import {Component, EventEmitter, forwardRef, Inject, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {ReplaySubject} from 'rxjs';
import {ControlValueAccessor, FormBuilder, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {DOCUMENT} from '@angular/common';
import {debounceTime, distinctUntilChanged, startWith, takeUntil} from 'rxjs/operators';
import {ListResponse} from '../../interface/response/list-response';
import {Ofwca} from '../../interface/ofwca';
import {OfwcaService} from '../../services/ofwca.service';
import {MatSelectChange} from '@angular/material/select';
import {InsuranceCompany} from '../../interface/insurance-company';

@Component({
  selector: 'app-dict-ofwcas',
  templateUrl: './dict-ofwcas.component.html',
  styleUrls: ['./dict-ofwcas.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, multi: true, useExisting: forwardRef(() => DictOfwcasComponent)}
  ]
})
export class DictOfwcasComponent implements OnInit, OnDestroy, ControlValueAccessor {

  @Input() label: string;
  @Input() multiple = false;
  @Input() control: FormControl = null;
  @Input() required = false;
  @Input() filterProfit = false;
  @Input() filterPartner = null;
  @Input() contractMandate = false;
  @Input() status = false;
  @Input() parent: string;
  @Input() statusOfwca = [];
  @Input() ofwcaItems: Ofwca[] = null;
  @Output() selectedOfwca: EventEmitter<Ofwca> = new EventEmitter<Ofwca>();
  @Input() minimalDisplay = false;

  externalControl = true;
  disabled = false;
  items: Ofwca[] = [];
  value?: string = null;
  ofwcaFilterCtrl: FormControl;
  itemsSubject: ReplaySubject<ListResponse<Ofwca>> = new ReplaySubject<ListResponse<Ofwca>>(1);

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private ofwcaService: OfwcaService,
    @Inject(DOCUMENT) private document: Document,
    private fb: FormBuilder,
  ) {
    this.ofwcaFilterCtrl = fb.control('');
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  ngOnInit(): void {
    if ( this.ofwcaItems !== null) {
      this.items = this.ofwcaItems;
    }
    if (this.control === null) {
      this.control = new FormControl('');
      this.externalControl = false;
    }

    this.control.valueChanges
      .pipe(
        startWith(this.control.value),
        takeUntil(this.destroyed$)
      )
      .subscribe((val: string) => {
        if (val) {
          let exists = false;
          this.items.forEach((ofwca: Ofwca) => {
            if (ofwca.uuid === val) {
              exists = true;
            }
          });

          if (!exists) {
            this.ofwcaService.loadNotCountedList(0, 9, {
              in$uuid: val,
              partner: this.filterPartner ? this.filterPartner : null,
            }, this.itemsSubject);
          }
        }
      });

    this.ofwcaFilterCtrl.valueChanges
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        takeUntil(this.destroyed$)
      )
      .subscribe((val) => {
        if (val) {
          this.ofwcaService.loadNotCountedList(0, 5, {
            context: val,
            profit: this.filterProfit ? true : null,
            contractMandate: this.contractMandate ? true : null,
            statusKey: this.statusOfwca ? this.statusOfwca : null,
            partner: this.filterPartner ? this.filterPartner : null,
          }, this.itemsSubject);
        } else if (this.control.value) {
          this.ofwcaService.loadNotCountedList(0, 9, {
            in$uuid: this.control.value,
            partner: this.filterPartner ? this.filterPartner : null,
          }, this.itemsSubject);
        }
      });

    this.itemsSubject
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: ListResponse<Ofwca>) => {
        if ( this.ofwcaItems === null) {
          if (!!this.parent){
            this.items = result.items.filter(item => item.partner?.uuid === this.parent);
          } else {
            this.items = result.items;
          }
        } else{
          this.items = this.ofwcaItems;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.externalControl) {
      return;
    }

    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }

    this.disabled = isDisabled;
  }

  writeValue(value: any): void {
    if (this.externalControl) {
      return;
    }

    this.control.setValue(value);
    this.value = value;
  }

  onSelectionChange(e: MatSelectChange): void {
    this.items.forEach((ofwca: Ofwca) => {
      if (ofwca.uuid === e.value) {
        this.selectedOfwca.emit(ofwca);
      }
    });

    this.value = e.value;
    this.onChange(this.value);
  }

  clearValue(): void {
    this.control.setValue(this.multiple ? [] : '');
    this.writeValue(this.multiple ? [] : '');
    this.onChange(this.value);
  }

}
